import { useQueryClient } from '@tanstack/react-query'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useState } from 'react'
import { type SubmitHandler, useForm } from 'react-hook-form'

import { ProblemGeneratingBenchmark } from './CensusBanners'
import { type BenchmarkFormProps, type Quote } from './types'
import { type GrapeAPIErrorType } from '../hooks/GrapeAPIError'
import { useCreateBenchmark } from '../hooks/useCreateBenchmark'
import { Banner } from '../reusable/Banner'
import { FormDateField, FormIntegerField, FormSelectField } from '../reusable/form/FormFields'

type Props = {
  prospectId: string;
  quoteId: string;
  onGoBackToCensus: () => void;
  onSkipBenchmarkGeneration: () => void;
  onSuccess: () => void;
}

export function GenerateBenchmarkForm ({
  prospectId,
  quoteId,
  quote,
  onGoBackToCensus,
  onSkipBenchmarkGeneration,
  onSuccess,
  onFailure,
  readOnly,
}: Props & {
  quote?: Quote;
  onFailure: (error: GrapeAPIErrorType) => void;
  readOnly?: boolean;
}) {
  const queryClient = useQueryClient()

  const createBenchmarkMutation = useCreateBenchmark({ prospectId, quoteId })

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<BenchmarkFormProps>({
    defaultValues: {
      target_individual_deductible: quote?.target_individual_deductible,
      target_individual_oop_max: quote?.target_individual_oop_max,
      target_network_type: quote?.target_network_type,
      target_metal_tier: quote?.target_metal_tier,
      coverage_start_date: quote?.coverage_start_date,
      year: quote?.year,
    },
  })

  const onSubmit: SubmitHandler<BenchmarkFormProps> = async (data) => {
    // check coverage start date
    if (new Date(data.coverage_start_date || '') < new Date()) {
      setError('coverage_start_date', { type: 'manual', message: 'Coverage start date must be in the future.' })

      return
    }

    createBenchmarkMutation.mutate(data, {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ['prospect', prospectId, 'quote', quoteId],
        })
        onSuccess()
      },
      onError: (error) => {
        onFailure(error)
      },
    })
  }

  return (
    <form
      className='flex flex-col justify-between gap-y-4 mt-6'
      onSubmit={handleSubmit(onSubmit)}
    >
      <div className='flex gap-x-4 justify-between'>
        <FormIntegerField<BenchmarkFormProps>
          disabled={createBenchmarkMutation.isPending || readOnly}
          error={errors.target_individual_deductible}
          fieldId='target_individual_deductible'
          fieldLabel='Deductible'
          register={register}
          validationRule={{
            required: 'This field is required',
          }}
        />
        <FormIntegerField<BenchmarkFormProps>
          disabled={createBenchmarkMutation.isPending || readOnly}
          error={errors.target_individual_oop_max}
          fieldId='target_individual_oop_max'
          fieldLabel='Out of pocket max'
          register={register}
          validationRule={{
            required: 'This field is required',
          }}
        />
      </div>
      <div className='flex gap-x-4 justify-between'>
        <FormSelectField<BenchmarkFormProps>
          disabled={createBenchmarkMutation.isPending || readOnly}
          error={errors.target_network_type}
          fieldId='target_network_type'
          fieldLabel='Network type'
          fieldOptions={[
            { label: 'Select', value: '' },
            { label: 'HMO', value: 'hmo' },
            { label: 'PPO', value: 'ppo' },
            { label: 'EPO', value: 'epo' },
            { label: 'POS', value: 'pos' },
          ]}
          register={register}
        />
        <FormSelectField<BenchmarkFormProps>
          disabled={createBenchmarkMutation.isPending || readOnly}
          error={errors.target_metal_tier}
          fieldId='target_metal_tier'
          fieldLabel='Metal level'
          fieldOptions={[
            { label: 'Select', value: '' },
            { label: 'Bronze', value: 'bronze' },
            { label: 'Silver', value: 'silver' },
            { label: 'Gold', value: 'gold' },
            { label: 'Platinum', value: 'platinum' },
          ]}
          register={register}
        />
      </div>
      <div className='flex gap-x-4 justify-between'>
        <FormDateField<BenchmarkFormProps>
          disabled={createBenchmarkMutation.isPending || readOnly}
          error={errors.coverage_start_date}
          fieldId='coverage_start_date'
          fieldLabel='Coverage start date'
          register={register}
          validationRule={{
            required: 'Coverage start date is required.'
          }}
        />
        <FormSelectField<BenchmarkFormProps>
          disabled={createBenchmarkMutation.isPending || readOnly}
          error={errors.year}
          fieldId='year'
          fieldLabel='Quote year'
          fieldOptions={[
            { label: '2026', value: 2026 },
            { label: '2025', value: 2025 },
            { label: '2024', value: 2024 },
          ]}
          register={register}
          validationRule={{
            required: 'Quote year is required.',
          }}
        />
      </div>
      {!readOnly && (
        <div className='flex items-center gap-x-2 mt-10'>
          <button
            className='btn-outline'
            type='button'
            disabled={createBenchmarkMutation.isPending}
            onClick={onGoBackToCensus}
          >
            <ChevronLeft size={16} className='mr-1' />
            Back
          </button>
          <button
            className='btn-outline'
            type='button'
            disabled={createBenchmarkMutation.isPending}
            onClick={onSkipBenchmarkGeneration}
          >
            Skip to coverage levels
            <ChevronRight size={16} className='ml-1' />
          </button>
          <button className='btn-primary' disabled={createBenchmarkMutation.isPending} type='submit'>
            Next
            <ChevronRight size={16} className='ml-1' />
          </button>
        </div>
      )}
    </form>
  )
}

export function QuoteCreateGenerateBenchmark ({
  prospectId,
  quoteId,
  onGoBackToCensus,
  onSkipBenchmarkGeneration,
  onSuccess,
}: Props) {
  const [error, setError] = useState<GrapeAPIErrorType | null>(null)

  return (
    <div className='flex gap-x-10'>
      <div className='flex-1'>
        <h2 className='h2 font-normal mb-1'>Set parameters</h2>
        <p className='text-ds-text-secondary text-xs'>Optional</p>
        <GenerateBenchmarkForm
          prospectId={prospectId}
          quoteId={quoteId}
          onSuccess={onSuccess}
          onFailure={setError}
          onGoBackToCensus={onGoBackToCensus}
          onSkipBenchmarkGeneration={onSkipBenchmarkGeneration}
        />
      </div>
      <div className='flex-1'>
        {error
          ? (
            <ProblemGeneratingBenchmark error={error} />
            )
          : (
            <Banner type='default' titleText='This step is optional'>
              <p>
                Provide the details for a benchmark plan. You can also skip this
                step. Setting parameters will help us suggest benchmarks to run
                the quote against.
              </p>
            </Banner>
            )}
      </div>
    </div>
  )
}
